import type { DocumentData } from 'firebase/firestore';
import type { AuditDoc, AuditSummary } from '@/types/models';
import { Cpu, MemoryStick, Zap, HardDrive, Network, Database, Boxes, Activity, FolderTree, Files, ListChecks, Server, Share2, Gauge, Sparkles, Braces, CalendarClock, FileQuestion } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { labelClassName } from '@/components/ui/label';
import { useT } from '@/lib/i18n';
import { fmtBytes, fmtGb, fmtMbps } from '@/lib/utils';
import type { TranslateFn } from '@/lib/utils';

/** Nombre de partages réseau (05 file_shares.network_shares) quand le JSON du module est inline. */
export function networkSharesCount(module05?: DocumentData): number | undefined {
  const shares = (module05?.json as { file_shares?: { network_shares?: unknown } } | undefined)?.file_shares?.network_shares;
  return Array.isArray(shares) ? shares.length : undefined;
}

interface StatCardProps { icon: React.ReactNode; label: string; value: string; sub?: string; accent?: boolean; warn?: boolean; title?: string }

const join = (parts: (string | null | undefined)[]): string | undefined =>
  parts.filter(Boolean).join(' · ') || undefined;

/**
 * KPI issus des clés du contrat 1.1 : chaque carte n'est construite que si sa
 * donnée existe, un audit 1.0 / legacy n'en affiche donc aucune (jamais de 0
 * inventé, jamais une rangée de tirets).
 */
function contractV11Cards(s: Partial<AuditSummary>, t: TranslateFn): StatCardProps[] {
  const cards: StatCardProps[] = [];

  const virtualization = s.isVm === true
    ? (s.hypervisor ? t('audit.kpi.machine.vm', { hv: s.hypervisor }) : t('audit.kpi.machine.vm_unknown'))
    : s.isVm === false ? t('audit.kpi.machine.physical') : null;
  if (s.machineModel || virtualization) {
    cards.push({
      icon: <Server className="h-4 w-4" />,
      label: t('audit.kpi.machine'),
      value: s.machineModel || virtualization || '—',
      sub: join([s.machineModel ? virtualization : null, s.cpuFeatures?.length ? t('audit.kpi.cpu_features', { list: s.cpuFeatures.slice(0, 4).join(' ') }) : null]),
      title: s.cpuFeatures?.join(' '),
    });
  }

  if (s.storageLocalGb != null || s.storageNetworkGb != null || s.storageLargestFreeGb != null) {
    cards.push({
      icon: <HardDrive className="h-4 w-4" />,
      label: t('audit.kpi.storage_local'),
      value: fmtGb(s.storageLocalGb),
      sub: join([
        s.storageNetworkGb != null ? t('audit.kpi.storage_network', { n: fmtGb(s.storageNetworkGb) }) : null,
        s.storageLargestFreeGb != null ? t('audit.kpi.storage_largest_free', { n: fmtGb(s.storageLargestFreeGb) }) : null,
      ]),
    });
  }

  if (s.sharesMounted != null || s.sharesHosted != null) {
    cards.push({
      icon: <Share2 className="h-4 w-4" />,
      label: t('audit.kpi.shares'),
      value: `${(s.sharesMounted ?? 0) + (s.sharesHosted ?? 0)}`,
      sub: join([
        s.sharesMounted != null ? t('audit.kpi.shares_mounted', { n: s.sharesMounted }) : null,
        s.sharesHosted != null ? t('audit.kpi.shares_hosted', { n: s.sharesHosted }) : null,
      ]),
    });
  }

  if (s.linkSpeedMbps != null) {
    cards.push({
      icon: <Gauge className="h-4 w-4" />,
      label: t('audit.kpi.link_speed'),
      value: fmtMbps(s.linkSpeedMbps),
      sub: t('audit.kpi.link_speed.sub'),
    });
  }

  if (Array.isArray(s.aiRuntimes)) {
    cards.push({
      icon: <Sparkles className="h-4 w-4" />,
      label: t('audit.kpi.ai_runtimes'),
      value: s.aiRuntimes.length > 0 ? s.aiRuntimes.join(', ') : t('audit.kpi.none_detected'),
      accent: s.aiRuntimes.length > 0,
      title: s.aiRuntimes.join(', '),
    });
  }

  if (Array.isArray(s.vectorDbs)) {
    cards.push({
      icon: <Braces className="h-4 w-4" />,
      label: t('audit.kpi.vector_dbs'),
      value: s.vectorDbs.length > 0 ? s.vectorDbs.join(', ') : t('audit.kpi.none_detected'),
      accent: s.vectorDbs.length > 0,
      title: s.vectorDbs.join(', '),
    });
  }

  if (s.scheduledJobs != null || s.backupsDetected != null) {
    cards.push({
      icon: <CalendarClock className="h-4 w-4" />,
      label: t('audit.kpi.scheduled_jobs'),
      value: s.scheduledJobs != null ? s.scheduledJobs.toLocaleString() : '—',
      sub: s.backupsDetected == null ? undefined : t(s.backupsDetected ? 'audit.kpi.backups.detected' : 'audit.kpi.backups.none'),
      warn: s.backupsDetected === false,
    });
  }

  const fsErrors = (s.fsStatErrors ?? 0) + (s.fsUnreadableDirs ?? 0);
  if (s.fsOther != null || s.fsPartial != null || s.fsStatErrors != null || s.fsUnreadableDirs != null) {
    cards.push({
      icon: <FileQuestion className="h-4 w-4" />,
      label: t('audit.kpi.fs_other'),
      value: s.fsOther != null ? s.fsOther.count.toLocaleString() : '—',
      sub: join([
        s.fsOther != null ? t('audit.kpi.fs_other.sub', { size: fmtBytes(s.fsOther.sizeBytes) }) : null,
        s.fsPartial == null ? null : t(s.fsPartial ? 'audit.kpi.fs_partial' : 'audit.kpi.fs_complete'),
        fsErrors > 0 ? t('audit.kpi.fs_errors', { errors: s.fsStatErrors ?? 0, dirs: s.fsUnreadableDirs ?? 0 }) : null,
      ]),
      warn: s.fsPartial === true || fsErrors > 0,
      title: s.fsUnknownExtensions?.length ? s.fsUnknownExtensions.join(' ') : undefined,
    });
  }

  return cards;
}

export function OverviewTab({ audit, modules = {} }: { audit: AuditDoc; modules?: Record<string, DocumentData> }) {
  const { t } = useT();
  const s: Partial<AuditSummary> = audit.summary ?? {};
  const dbCount = s.dbReachable?.length;
  const shares = networkSharesCount(modules['05_data_sources']);
  const dataSources = dbCount == null && shares == null ? undefined : (dbCount ?? 0) + (shares ?? 0);
  const extraCards = contractV11Cards(s, t);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Cpu className="h-4 w-4" />}
          label={t('audit.kpi.cpu')}
          value={s.cpuModel ?? (s.cpuCores != null ? t('audit.kpi.cores', { n: s.cpuCores }) : '—')}
          sub={join([
            s.cpuModel && s.cpuCores != null ? t('audit.kpi.cores', { n: s.cpuCores }) : null,
            s.cpuCoresPhysical != null ? t('audit.kpi.cores_physical', { n: s.cpuCoresPhysical }) : null,
            s.cpuSockets != null ? t('audit.kpi.sockets', { n: s.cpuSockets }) : null,
          ])}
        />
        <StatCard
          icon={<MemoryStick className="h-4 w-4" />}
          label={t('audit.kpi.ram')}
          value={fmtGb(s.ramGb)}
          sub={s.os}
        />
        <StatCard
          icon={<Zap className="h-4 w-4" />}
          label={t('audit.kpi.gpu')}
          value={s.gpuCount != null ? `${s.gpuCount}` : '—'}
          sub={join([
            s.gpuModel,
            s.vramGb != null ? t('audit.kpi.vram', { n: s.vramGb }) : null,
          ])}
        />
        <StatCard
          icon={<HardDrive className="h-4 w-4" />}
          label={t('audit.kpi.storage')}
          value={fmtGb(s.storageGb)}
          sub={s.storageFreeGb != null ? t('audit.kpi.storage_free', { n: fmtGb(s.storageFreeGb) }) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Network className="h-4 w-4" />}
          label={t('audit.kpi.nics')}
          value={s.networkInterfaces?.toString() ?? '—'}
        />
        <StatCard
          icon={<Boxes className="h-4 w-4" />}
          label={t('audit.kpi.containers')}
          value={s.containers?.toString() ?? '—'}
        />
        <StatCard
          icon={<Database className="h-4 w-4" />}
          label={t('audit.kpi.data_sources')}
          value={dataSources?.toString() ?? '—'}
          sub={join([
            dbCount != null ? t('audit.kpi.dbs', { n: dbCount }) : null,
            shares != null ? t('audit.kpi.shares_network', { n: shares }) : null,
          ])}
          title={s.dbReachable?.join(', ')}
        />
        <StatCard
          icon={<Activity className="h-4 w-4" />}
          label={t('audit.kpi.ai_readiness')}
          value={s.aiReadinessScore != null ? `${s.aiReadinessScore}/100` : '—'}
          sub={s.aiReadinessScore != null ? t('audit.kpi.score.sub') : undefined}
          accent
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<Files className="h-4 w-4" />}
          label={t('audit.kpi.data_volume')}
          value={fmtGb(s.dataFilesGb)}
          sub={s.dataFiles != null ? t('audit.kpi.data_files', { n: s.dataFiles.toLocaleString() }) : undefined}
        />
        <StatCard
          icon={<FolderTree className="h-4 w-4" />}
          label={t('audit.kpi.fs')}
          value={s.fsFiles?.toLocaleString() ?? '—'}
          sub={s.fsDirs != null ? t('audit.kpi.fs_dirs', { n: s.fsDirs.toLocaleString() }) : undefined}
        />
        <StatCard
          icon={<ListChecks className="h-4 w-4" />}
          label={t('audit.kpi.modules')}
          value={s.modulesTotal != null ? `${s.modulesOk ?? 0}/${s.modulesTotal}` : '—'}
          sub={s.modulesTotal != null ? t('audit.kpi.modules.sub', { ok: s.modulesOk ?? 0, total: s.modulesTotal }) : undefined}
        />
      </div>

      {extraCards.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {extraCards.map(card => <StatCard key={card.label} {...card} />)}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, warn, title }: StatCardProps) {
  const tone = accent
    ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300'
    : warn
      ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-slate-100 text-slate-500 dark:bg-ink-700 dark:text-ink-300';
  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className={labelClassName()}>{label}</div>
        <div className={`grid h-7 w-7 place-items-center rounded-md ${tone}`}>{icon}</div>
      </div>
      <div className="mt-2 truncate font-display text-xl text-ink-900 dark:text-white" title={title ?? value}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-xs text-ink-500" title={sub}>{sub}</div>}
    </Card>
  );
}
